/**
 * Sale promotion state (e.g. Diwali Double-up), read from GET /api/promotions/current.
 *
 * The backend is the source of truth for prices and rewards; this mirrors it
 * so on-screen totals match what's charged. `usePromotion()` loads it once
 * (cached for 5 minutes) and also stores it in a module-level cache, so plain
 * pricing helpers (getAutoDiscount, etc.) can read it synchronously — any
 * component that calls usePromotion() re-renders once it arrives.
 */
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getApiBase } from './apiBase';

let current = null;

export const DAY_PASS_OFFER = 'day_pass_3_plus_3';
export const MEETING_ROOM_OFFER = 'meeting_room_2_plus_2';
export const CREDITS_2X_OFFER = 'credits_2x';

export const getPromotion = () => current;

export const otherDiscountsSuppressed = () => !!current?.other_discounts_suppressed;

export const offerLive = (key) => !!current?.offers?.[key]?.live;

export const usePromotion = () => {
  const { data } = useQuery({
    queryKey: ['promotion-current'],
    queryFn: async () => {
      const res = await axios.get(`${getApiBase()}/api/promotions/current`);
      current = res.data;
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  return data || current;
};

/** Free Day Passes a paid quantity earns: buy X, get X free from a minimum of
 * `block` (3 → 3, 4 → 4, 7 → 7) — mirrors promotions.day_pass_free_quantity. */
export const dayPassFreeQuantity = (paidQty) => {
  if (!offerLive(DAY_PASS_OFFER)) return 0;
  const minimum = Math.max(1, current.offers[DAY_PASS_OFFER].block || 3);
  const qty = Math.max(0, paidQty || 0);
  return qty >= minimum ? qty : 0;
};

export const MEETING_ROOM_TYPES = ['meeting_room', 'conference_room'];

/** Free hours a booking earns: a flat 2 once it's 2+ paid hours (capped per
 * booking, not multiplied) — mirrors promotions.meeting_room_free_hours. */
export const meetingRoomFreeHours = (hours) => {
  if (!offerLive(MEETING_ROOM_OFFER)) return 0;
  const block = Math.max(1, current.offers[MEETING_ROOM_OFFER].block || 2);
  return (hours || 0) >= block ? block : 0;
};

/**
 * Meeting Room 2+2 preview for a booking: paid hours/amount, the credit value
 * of those hours, the bonus credits for the free hours (same room rate) and the
 * total. Display only — the backend's numbers are what gets credited.
 */
export const meetingRoomRewardPreview = ({ bookableType, hours, paidAmount, paidCredits }) => {
  if (!MEETING_ROOM_TYPES.includes(bookableType) || !offerLive(MEETING_ROOM_OFFER) || !hours) return null;
  const freeHours = meetingRoomFreeHours(hours);
  const block = current.offers[MEETING_ROOM_OFFER].block || 2;
  if (freeHours <= 0) return { teaser: true, block, saleName: current.name, validUntil: current.reward_valid_until };
  const bonusCredits = paidCredits > 0 ? +((paidCredits / hours) * freeHours).toFixed(1) : null;
  return {
    paidHours: hours,
    freeHours,
    paidAmount,
    paidCredits,
    bonusCredits,
    totalCredits: bonusCredits != null ? +(paidCredits + bonusCredits).toFixed(1) : null,
    saleName: current.name,
    validUntil: current.reward_valid_until,
  };
};

/** Backend promo_reward (offer meeting_room_2_plus_2) → MeetingRoomRewardPanel shape. */
export const meetingRoomRewardFromBackend = (pr, paidAmount) => (
  pr && pr.offer === MEETING_ROOM_OFFER ? {
    paidHours: pr.paid_hours,
    freeHours: pr.free_hours,
    paidAmount,
    paidCredits: pr.paid_credits_value,
    bonusCredits: pr.bonus_credits,
    totalCredits: pr.total_credits_value,
    saleName: current?.name,
    validUntil: pr.valid_until,
  } : null
);

/** Bonus credits a purchase earns under the 2X offer — mirrors promotions.credit_bonus. */
export const creditPurchaseBonus = (credits, user) => {
  if (!offerLive(CREDITS_2X_OFFER) || !user || user.is_guest || user.role !== 'client') return 0;
  const m = current.offers[CREDITS_2X_OFFER].multiplier || 1;
  return Math.max(0, Math.round(credits * (m - 1)));
};
